// src/features/orders/pickupSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ✅ Define the pickup item type
interface Pickup {
  id: string;
  status: string;
  [key: string]: any; // allow extra fields
}

// ✅ Define the state type
interface PickupState {
  list: any[]
  loading: boolean;
  error: string | null;
}

// ✅ Define update payload type
interface PickupStatusUpdatePayload {
  pickupId: string;
  status: string;
  driverName: string;
  staffName: string;
}

// ✅ Initial state
const initialState: PickupState = {
  list: [],
  loading: false,
  error: null,
};

// ✅ Slice
const pickupSlice = createSlice({
  name: 'pickup',
  initialState,
  reducers: {
    setPickupList(state, action: PayloadAction<any[]>) {
      state.list = action.payload;
      state.loading = false;
    },

    addPickup(state: any, action: PayloadAction<Pickup>) {
      const newPickup = action.payload;

      // Case 1: flat list
      if (Array.isArray(state.list)) {
        state?.list?.unshift(newPickup); // add at the start
        if (state.list.length > 10) {
          state.list.pop(); // remove last to keep only 10
        }

        // Case 2: nested pickupRequests
      } else if (state.list && Array.isArray(state.list.pickupRequests)) {
        state?.list?.pickupRequests?.unshift(newPickup); // add at the start
        if (state.list.pickupRequests.length > 10) {
          state.list.pickupRequests.pop(); // remove last
        }

        // Case 3: fallback
      } else {
        state.list = [newPickup];
      }
    },

    pickupStatusUpdate(state: any, action: PayloadAction<PickupStatusUpdatePayload>) {
      const { pickupId, status, driverName, staffName } = action.payload;

      if (!pickupId) {
        console.error("❌ pickupStatusUpdate: pickupId is missing", action.payload);
        return;
      }

      if (Array.isArray(state.list)) {
        const pickup = state.list.find((p: any) => p.id === pickupId);
        if (pickup) {
          pickup.status = status;
          if (driverName) {
            pickup.driverName = driverName;
          }

          if (staffName) {
            pickup.staffName = staffName;
          }
        }
      } else if (state.list && Array.isArray(state.list.pickupRequests)) {
        const pickup = state.list.pickupRequests.find((p: any) => p.pickupId === pickupId);
        if (pickup) {
          pickup.status = status;
          if (driverName) {
            pickup.driverName = driverName;
          }

          if (staffName) {
            pickup.staffName = staffName;
          }
        }
      }
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});


export const {
  setPickupList,
  pickupStatusUpdate,
  setLoading,
  setError,
  addPickup
} = pickupSlice.actions;

export default pickupSlice.reducer;
